"use client";

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as klinecharts from "klinecharts";
import { useChartStore3 } from "@/vybe-trader-client/chart/stores/useChartStore3";
import { pickLiveCandlePrice } from "@/vybe-trader-client/chart/stores/livePrice";
import { useChartVisibilityGate } from "@/components/chart/useChartVisibilityGate";
import { useChartPanelId } from "@/components/chart/ChartPanelContext";
import { Loader2 } from "lucide-react";
import { useChartSettingsStore } from "@/lib/chart-settings";
import { generateChartStyles } from "@/lib/chart-helpers";
import { createDrawing } from "@/lib/chart-helpers/chart-drawings";
import { useDrawingsStore } from "@/lib/chart-drawings";
import { useChartDrawings } from "@/lib/chart-helpers/hooks/useChartDrawings";
import type { DrawingType } from "@/lib/chart-drawings/types";
import { useTradeHistoryLines } from "@/lib/chart-helpers/hooks/useTradeHistoryLines";
import { useChartTradeOverlay } from "@/lib/chart-helpers/trade-overlay/useChartTradeOverlay";
import { useMainChartStore } from "@/lib/main-chart";
import {
  clearIndicatorSyncState,
  forceChartIndicatorRecalc,
  syncIndicators,
  type ChartIndicatorApi,
  type IndicatorSyncItem,
  type IndicatorSyncStateMap,
} from "@/lib/chart-helpers/chart-indicators";
import { setCustomIndicatorComputeCallback, clearCustomComputeCache } from "@/lib/custom-indicators/compiled-template";
import { useChartIndicatorsStore } from "@/lib/chart-indicators";
import { useIndicatorCompileStore } from "@/lib/custom-indicators/runtime/compile-store";
import {
  registerChartPanel,
  unregisterChartPanel,
} from "@/lib/chart-runtime/chart-instance-registry";
import { useOpenedTradesStore } from "@/vybe-trader-client/opened-trades/stores/useOpenedTradesStore";
import { TradeOverlayNew } from "./new";
import { SpecificTradePanel } from "./SpecificTradePanel";
import { ChartTradePanel } from "./chart-trade-panel";
import { ChartMarketDepthPanel } from "./market-depth";
import {
  createBidCountdownOverlay,
  formatRemainingMs,
  getNextCandleCloseMs,
  registerBidCountdownOverlay,
  removeBidCountdownOverlay,
  updateBidCountdownOverlay,
} from "@/lib/chart-helpers/custom-overlays/bidCountdownOverlay";
import { useSubscribedSymbolsStore } from "@/vybe-trader-client/subscribed-symbols/stores/useSubscribedSymbolsStore";
import type { Quote } from "@/vybe-trader-client/subscribed-symbols/types";
import { getCandleTimestamp } from "@/lib/chart-helpers/kline-helpers-new";
import { AddTextDialog } from "./AddTextDialog";
import { useVybeTraderClient } from "@/vybe-trader-client/connection/hooks/useVybeTraderClient";
import { useSettingsStore } from "@/lib/settings";
import {
  VybeOrderType,
} from "@/vybe-trader-client/trade-history/types";
import { getTradeClosePrice } from "@/vybe-trader-client/opened-trades";
import { buildModifyPayloadForSlTp } from "@/lib/terminal/engine/shared/orders";
import { OneClickTradingDialog } from "@/components/dialogs/OneClickTradingDialog";
import { useIsInvestorAccount } from "@/vybe-trader-client/account-information/hooks/useAccountInformation";

/**
 * X-axis tick date format per selected timeframe (FIX G): intraday traders
 * need time-of-day resolution, not repeated dates. Crosshair/tooltip rows keep
 * the full date-time via the default format.
 */
function getAxisTickFormat(timeframe: string): string {
  switch (timeframe) {
    case "1m":
      return "HH:mm:ss";
    case "5m":
    case "15m":
    case "30m":
    case "1h":
    case "2h":
      return "HH:mm";
    case "4h":
    case "12h":
      return "MM-DD HH:mm";
    default:
      // 1d / 1w / 1mn
      return "YYYY-MM-DD";
  }
}

/** Install a timeframe-aware formatDate custom API on a chart instance. */
function applyTimeframeDateFormat(chart: unknown, timeframe: string): void {
  const chartAny = chart as any;
  if (typeof chartAny?.setCustomApi !== "function") return;
  const axisFormat = getAxisTickFormat(timeframe);
  chartAny.setCustomApi({
    formatDate: (
      dateTimeFormat: Intl.DateTimeFormat,
      timestamp: number,
      format: string,
      type: number
    ) => {
      // FormatDateType.XAxis === 2 — axis ticks get the short timeframe-aware
      // format; crosshair (1) and tooltip (0) keep the full date-time format.
      const effectiveFormat = type === 2 ? axisFormat : format;
      return (klinecharts as any).utils.formatDate(
        dateTimeFormat,
        timestamp,
        effectiveFormat
      );
    },
  });
}

interface ChartProps {
  selectedObjectType: string;
  onObjectAdded: () => void;
  isCrosshairVisible?: boolean;
  isSpecificTradePanelVisible?: boolean;
  setIsSpecificTradePanelVisible?: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  onOrderLinePriceChange?: (orderType: string, price: number) => void;
  isDesktop?: boolean;
  isTradePanelVisible?: boolean;
  isMarketDepthVisible?: boolean;
}

// LoadDataType enum values (if not exported from klinecharts)
const LoadDataType = {
  Init: "init",
  Forward: "forward",
  Backward: "backward",
} as const;

const PENDING_ENTRY_DRAG_ORDER_TYPES = new Set<number>([
  VybeOrderType.BuyLimit,
  VybeOrderType.SellLimit,
  VybeOrderType.BuyStop,
  VybeOrderType.SellStop,
]);

const PENDING_ORDER_TYPES = new Set<number>([
  VybeOrderType.BuyLimit,
  VybeOrderType.SellLimit,
  VybeOrderType.BuyStop,
  VybeOrderType.SellStop,
  VybeOrderType.BuyStopLimit,
  VybeOrderType.SellStopLimit,
]);

/**
 * Shared width for desktop chart overlays (trading panel + market depth ladder).
 * Both panels render at this exact width so toggling either one keeps the
 * other column-aligned and avoids visual jitter when switching symbols.
 */
const CHART_DESKTOP_OVERLAY_WIDTH_PX = 340;

export default function Chart({
  selectedObjectType,
  onObjectAdded,
  isCrosshairVisible = false,
  isSpecificTradePanelVisible = false,
  setIsSpecificTradePanelVisible,
  onOrderLinePriceChange,
  isDesktop = false,
  isTradePanelVisible = false,
  isMarketDepthVisible = false,
}: ChartProps) {
  const mainChartIndicators = useMainChartStore(
    (state) => state.mainChart.indicators
  );
  const separatePaneIndicators = useChartIndicatorsStore(
    (state) => state.indicators.chartIndicators
  );
  const [customWasmComputeTick, setCustomWasmComputeTick] = useState(0);
  const customCompileRevisionKey = useIndicatorCompileStore(
    (s) => s.globalRevision
  );
  const allTrades = useOpenedTradesStore((state) => state.allTrades);

  useEffect(() => {
    setCustomIndicatorComputeCallback(() => {
      setCustomWasmComputeTick((t) => t + 1);
    });
    return () => setCustomIndicatorComputeCallback(null);
  }, []);

  const objects = useDrawingsStore((state) => state.objects);
  const addChartObject = useDrawingsStore((state) => state.addDrawing);
  const updateChartObjectSettings = useDrawingsStore(
    (state) => state.updateDrawingSettings
  );
  const getObjectsBySymbol = useDrawingsStore(
    (state) => state.getDrawingsBySymbol
  );
  const [showTextModal, setShowTextModal] = useState(false);
  const [textValue, setTextValue] = useState("");
  // Captures the drawing type that triggered the text-prompt modal, so that
  // handleTextSubmit can recreate the correct drawing (e.g. textLabel) rather
  // than hard-coding a plain "text" drawing. useChartDrawings clears the
  // selected type via onDrawingAdded right after opening the modal, so we
  // record it at open time via the wrapped setter below.
  const pendingTextDrawingTypeRef = useRef<DrawingType>("text" as DrawingType);
  const openTextModal = useCallback(
    (show: boolean) => {
      if (show) {
        pendingTextDrawingTypeRef.current =
          (selectedObjectType as DrawingType) || ("text" as DrawingType);
      }
      setShowTextModal(show);
    },
    [selectedObjectType]
  );
  const [selectedTradeTicket, setSelectedTradeTicket] = useState<string | null>(
    null
  );
  type TradeOverlayPlacementHints = {
    activateSl?: boolean;
    activateTp?: boolean;
  };
  const [tradeOverlayPlacementHints, setTradeOverlayPlacementHints] =
    useState<TradeOverlayPlacementHints | null>(null);
  const [pendingCloseTradeTicket, setPendingCloseTradeTicket] = useState<
    string | null
  >(null);
  const [showOneClickDialog, setShowOneClickDialog] = useState(false);
  const { client } = useVybeTraderClient();
  // Investor (read-only) login: trade lines are display-only and the trade popovers never mount.
  const isInvestor = useIsInvestorAccount();
  // Avoid subscribing to the whole settings store from Chart - the only value
  // read here is `oneClickTrading`, and it is read inside a click handler, so
  // we can read it lazily via `getState()` without forcing Chart to re-render
  // whenever any unrelated setting changes.

  const chartPanelId = useChartPanelId();

  const selectedSymbol = useChartStore3(
    (state) => state.slots[chartPanelId].selectedSymbol,
  );
  const selectedTimeframe = useChartStore3(
    (state) => state.slots[chartPanelId].selectedTimeframe,
  );
  const ohlcRevision = useChartStore3(
    (state) => state.slots[chartPanelId].ohlcRevision,
  );
  const tradeFocusGeneration = useChartStore3(
    (state) => state.tradeFocusGeneration,
  );
  // Proposal 65: per-panel resync-gate state for the header dot + overlay chip.
  const chartFreshness = useChartStore3(
    (state) => state.slots[chartPanelId].chartFreshness,
  );
  const backfilledBars = useChartStore3(
    (state) => state.slots[chartPanelId].backfilledBars,
  );

  useEffect(() => {
    useChartStore3.getState().setFocusedTradeTicket(
      chartPanelId,
      selectedTradeTicket,
    );
  }, [selectedTradeTicket, chartPanelId]);

  useEffect(() => {
    return () => {
      useChartStore3.getState().setFocusedTradeTicket(chartPanelId, null);
    };
  }, [chartPanelId]);

  const dataLoadStateRef = useRef({
    newestTimestamp: 0,
    isInitialized: false,
  });

  // Bumped each time a fresh OHLC dataset is applied to the KLineCharts
  // instance. We can't rely on `ohlcRevision` for this signal because cached
  // timeframe switches resolve `getInitialData` synchronously without touching
  // the store's revision counter — drawings would never get a chance to
  // re-render against the new data list and future-anchored points would
  // collapse to "infinite future".
  const [chartDataNonce, setChartDataNonce] = useState(0);
  const bumpChartDataNonce = useCallback(() => {
    setChartDataNonce((n) => n + 1);
  }, []);

  /** Apply OHLC only when the chart instance and symbol still match this panel. */
  const applyInitialDataIfCurrent = useCallback(
    (
      chart: ChartIndicatorApi,
      chartToken: unknown,
      expectedCurrency: string,
      initialData: Array<{ timestamp: number }>
    ) => {
      if (chartInstanceRef.current !== chartToken) return false;
      const liveCurrency =
        useChartStore3.getState().slots[chartPanelId].selectedSymbol?.currency;
      if (!liveCurrency || liveCurrency !== expectedCurrency) return false;
      if (initialData.length === 0) return false;

      dataLoadStateRef.current.newestTimestamp =
        initialData[initialData.length - 1]!.timestamp;
      dataLoadStateRef.current.isInitialized = true;
      (chart as unknown as { applyNewData: (data: unknown[]) => void }).applyNewData(
        initialData
      );
      bumpChartDataNonce();
      return true;
    },
    [chartPanelId, bumpChartDataNonce]
  );

  // Live quote is consumed imperatively (see the subscription effect below) so a
  // burst of quote ticks does not re-render this large component. Only the
  // rAF-throttled `displayQuote` React state — read by children that genuinely
  // render the quote (trade overlay, SpecificTradePanel) — updates per frame,
  // and only when bid/ask actually change. Per-tick chart work (live candle,
  // ask/bid lines, bid countdown) runs off `currentQuoteRef` in the flush.
  type LiveQuote = Quote;
  const [displayQuote, setDisplayQuote] = useState<LiveQuote | null>(null);

  // Extract current quote (React-rendered snapshot; may lag the ref by <1 frame)
  const currentQuote = displayQuote;

  // Track ask/bid line IDs for updates
  const askLineIdRef = useRef<string | null>(null);
  const bidLineIdRef = useRef<string | null>(null);
  const bidCountdownOverlayIdRef = useRef<string | null>(null);
  const bidCountdownLatestBidRef = useRef<number>(0);
  // Mirror the latest quote so the ask/bid lifecycle effect can read it on
  // creation without subscribing to it (recreating overlays on every tick).
  const currentQuoteRef = useRef<typeof currentQuote>(null);

  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstanceRef = useRef<any | null>(null);
  const mainIndicatorSyncRef = useRef<IndicatorSyncStateMap>(new Map());
  const separateIndicatorSyncRef = useRef<IndicatorSyncStateMap>(new Map());
  /**
   * Set to true synchronously when the user interacts with a trade overlay
   * (click/double-click/drag). The chart-container click handler reads this
   * flag and skips the "deselect on background click" path so that clicking
   * a trade element does not unfocus that trade.
   */
  const tradeInteractionRef = useRef(false);
  /** Tracks last applied chart symbol currency for clearing trade selection on manual symbol change. */
  const tradeChartPrevCurrencyRef = useRef<string | null>(null);
  /** Throttle live quote → candle updates on coarse TFs (reduces chart/React churn). */
  const lastLiveCandleApplyRef = useRef(0);
  /**
   * RP-08: set true right after a fresh klinecharts instance is created so the
   * volume effect can skip its remove-all VOL sweep (a brand-new chart has no
   * VOL indicators to remove). Consumed (reset to false) on the volume effect's
   * next run.
   */
  const chartFreshlyCreatedRef = useRef(false);
  // Mirror crosshair mode in a ref so the init effect can re-apply scroll/zoom
  // lock after a fresh chart is created (symbol/timeframe change) without
  // adding `isCrosshairVisible` to the init effect's deps (which would force a
  // full chart dispose+recreate on every toggle).
  const isCrosshairVisibleRef = useRef(isCrosshairVisible);
  useEffect(() => {
    isCrosshairVisibleRef.current = isCrosshairVisible;
  }, [isCrosshairVisible]);

  // currentQuoteRef is kept in sync imperatively by the quote subscription
  // effect below (not from `currentQuote`), so per-tick chart work never waits
  // on a React render.

  const chartType = useChartSettingsStore(
    (state) => state.chartSettings.chartType
  );
  const chartStyle = useChartSettingsStore(
    (state) => state.chartSettings.chartStyle
  );

  // Pobierz wartość tradePositions
  const showTradePositions = useChartSettingsStore(
    (state) => state.chartSettings.features.tradePositions
  );

  // Pobierz wartość tradeOrders
  const showTradeOrders = useChartSettingsStore(
    (state) => state.chartSettings.features.tradeOrders
  );

  // Pobierz wartość tradeHistory
  const showTradeHistory = useChartSettingsStore(
    (state) => state.chartSettings.features.tradeHistory
  );

  // Pobierz wartość askPriceLine
  const showAskPriceLine = useChartSettingsStore(
    (state) => state.chartSettings.features.askPriceLine
  );

  // Pobierz wartość slTpLevels
  const showSlTpLevels = useChartSettingsStore(
    (state) => state.chartSettings.features.slTpLevels
  );

  const isPipsVisible = useChartSettingsStore(
    (state) => state.chartSettings.features.isPipsVisible
  );

  // Pobierz wartość volumes
  const showVolumes = useChartSettingsStore(
    (state) => state.chartSettings.features.volumes
  );

  const showCountdownToBarClose = useChartSettingsStore(
    (state) => state.chartSettings.features.countdownToBarClose
  );

  // OHLC legend toggle ("Time / Open / High / Low / Close / Volume")
  const showOhlcLegend = useChartSettingsStore(
    (state) => state.chartSettings.features.ohlc
  );

  const chartStyles = useMemo(() => {
    const baseStyles = generateChartStyles(
      chartStyle,
      chartType,
      undefined,
      showOhlcLegend
    );
    const baseCrosshair = (baseStyles as any).crosshair ?? {};
    const baseHorizontal = baseCrosshair.horizontal ?? {};
    const baseVertical = baseCrosshair.vertical ?? {};
    return {
      ...baseStyles,
      crosshair: {
        ...baseCrosshair,
        show: isCrosshairVisible,
        horizontal: {
          ...baseHorizontal,
          show: isCrosshairVisible,
        },
        vertical: {
          ...baseVertical,
          show: isCrosshairVisible,
        },
      },
    };
  }, [chartStyle, chartType, showOhlcLegend, isCrosshairVisible]);

  // Get ask/bid line colors from chart style
  const askLineColor = chartStyle.askPriceLine;
  const bidLineColor = chartStyle.bidPriceLine;

  const handleTradeOverlayInteraction = useCallback(() => {
    tradeInteractionRef.current = true;
  }, []);

  const handleConsumeTradeOverlayPlacementHints = useCallback(() => {
    setTradeOverlayPlacementHints(null);
  }, []);

  const handleChartBackgroundClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      // Only treat clicks that landed on a chart canvas as "background"
      // clicks. Clicks on overlay panels, dialogs, buttons, etc. should not
      // toggle trade focus.
      const target = event.target as HTMLElement | null;
      if (!target || target.tagName !== "CANVAS") {
        return;
      }

      if (tradeInteractionRef.current) {
        tradeInteractionRef.current = false;
        return;
      }

      setSelectedTradeTicket(null);
    },
    []
  );

  const onTradeLineClick = useCallback(
    (ticket: string) => {
      setSelectedTradeTicket(ticket);
      if (setIsSpecificTradePanelVisible) {
        setIsSpecificTradePanelVisible(false);
      }
    },
    [setIsSpecificTradePanelVisible]
  );

  const onTradeLineDoubleClick = useCallback(
    (ticket: string) => {
      setSelectedTradeTicket(ticket);
      if (setIsSpecificTradePanelVisible) {
        setIsSpecificTradePanelVisible(false);
      }
    },
    [setIsSpecificTradePanelVisible]
  );

  const getCurrentClosePrice = useCallback((trade: (typeof allTrades)[number]) => {
    const subscribedSymbol = useSubscribedSymbolsStore
      .getState()
      .byCurrency.get(trade.symbol);
    const quote = subscribedSymbol?.quote;

    return getTradeClosePrice(trade, quote);
  }, []);

  const executeCloseTrade = useCallback(
    async (ticket: string) => {
      if (!client || !client.isConnected()) {
        console.error("Client not connected");
        return;
      }

      const tradeToClose = useOpenedTradesStore
        .getState()
        .allTrades.find((trade) => trade.ticket.toString() === ticket);

      if (!tradeToClose) {
        console.error("Trade not found in store");
        return;
      }

      try {
        const isPending = PENDING_ORDER_TYPES.has(tradeToClose.orderType);
        await client.closeOrder({
          Ticket: tradeToClose.ticket,
          Symbol: tradeToClose.symbol,
          Price: isPending
            ? tradeToClose.openPrice
            : getCurrentClosePrice(tradeToClose),
          Lots: tradeToClose.lots,
          OrderType: tradeToClose.orderType,
          Slippage: isPending ? 0 : 100,
        });

        if (selectedTradeTicket === ticket) {
          setSelectedTradeTicket(null);
        }
      } catch (error) {
        console.error("Failed to close trade:", error);
      }
    },
    [client, getCurrentClosePrice, selectedTradeTicket]
  );

  const onTradeLineClose = useCallback(
    (ticket: string) => {
      const oneClickTrading =
        useSettingsStore.getState().settings.oneClickTrading;
      if (!oneClickTrading) {
        setPendingCloseTradeTicket(ticket);
        setShowOneClickDialog(true);
        return;
      }

      void executeCloseTrade(ticket);
    },
    [executeCloseTrade]
  );

  const onPendingEntryPriceChange = useCallback(
    async (ticket: string, price: number) => {
      if (!client || !client.isConnected()) {
        console.error("Client not connected");
        return;
      }

      const pendingOrder = useOpenedTradesStore
        .getState()
        .allTrades.find((trade) => trade.ticket.toString() === ticket);

      if (!pendingOrder) {
        console.error("Pending order not found in store");
        return;
      }

      if (!PENDING_ENTRY_DRAG_ORDER_TYPES.has(pendingOrder.orderType)) {
        return;
      }

      try {
        await client.modifyOrder({
          Ticket: pendingOrder.ticket,
          Symbol: pendingOrder.symbol,
          Lots: pendingOrder.lots,
          Price: price,
          OrderType: pendingOrder.orderType,
          StopLoss: pendingOrder.stopLoss || 0,
          TakeProfit: pendingOrder.takeProfit || 0,
          Comment: "Modified pending order price via chart drag",
        });
      } catch (error) {
        console.error("Failed to modify pending order price:", error);
      }
    },
    [client]
  );

  const submitSlTpModify = useCallback(
    async (
      ticket: string,
      next: { stopLoss?: number; takeProfit?: number },
      comment: string
    ) => {
      if (!client || !client.isConnected()) {
        console.error("Client not connected");
        return;
      }

      const trade = useOpenedTradesStore
        .getState()
        .allTrades.find((row) => row.ticket.toString() === ticket);

      if (!trade) {
        console.error("Trade not found in store");
        return;
      }

      try {
        // Through the shared builder so a dragged-off level carries ClearStopLoss/ClearTakeProfit —
        // a bare 0 reads as "unchanged" and the level comes straight back on the next snapshot.
        await client.modifyOrder(
          buildModifyPayloadForSlTp({
            ticket: trade,
            stopLoss: next.stopLoss,
            takeProfit: next.takeProfit,
            comment,
          })
        );
      } catch (error) {
        console.error("Failed to modify SL/TP:", error);
      }
    },
    [client]
  );

  const onTpLineClose = useCallback(
    (ticket: string) => {
      void submitSlTpModify(
        ticket,
        { takeProfit: 0 },
        "Removed take profit via chart"
      );
    },
    [submitSlTpModify]
  );

  const onSlLineClose = useCallback(
    (ticket: string) => {
      void submitSlTpModify(
        ticket,
        { stopLoss: 0 },
        "Removed stop loss via chart"
      );
    },
    [submitSlTpModify]
  );

  const onTpLinePriceChange = useCallback(
    (ticket: string, price: number) => {
      void submitSlTpModify(
        ticket,
        { takeProfit: price },
        "Modified take profit via chart drag"
      );
    },
    [submitSlTpModify]
  );

  const onSlLinePriceChange = useCallback(
    (ticket: string, price: number) => {
      void submitSlTpModify(
        ticket,
        { stopLoss: price },
        "Modified stop loss via chart drag"
      );
    },
    [submitSlTpModify]
  );

  const handleOneClickDialogChange = useCallback((open: boolean) => {
    setShowOneClickDialog(open);
    if (!open) {
      setPendingCloseTradeTicket(null);
    }
  }, []);

  const handleOneClickCloseConfirm = useCallback(() => {
    if (!pendingCloseTradeTicket) {
      return;
    }

    void executeCloseTrade(pendingCloseTradeTicket).finally(() => {
      setPendingCloseTradeTicket(null);
    });
  }, [executeCloseTrade, pendingCloseTradeTicket]);

  useEffect(() => {
    if (!selectedSymbol || !chartRef.current) {
      return;
    }

    const chart = klinecharts.init(chartRef.current);
    if (!chart) {
      return;
    }

    chartInstanceRef.current = chart;
    chartFreshlyCreatedRef.current = true;
    registerChartPanel(chartPanelId, chart);
    chart.setPriceVolumePrecision(selectedSymbol.digits, 2);
    chart.resize();
    chart.setStyles(chartStyles as any);

    // Crosshair no longer locks navigation — scroll/zoom stay enabled with
    // the crosshair visible (FIX G).
    const chartAny = chart as any;
    if (typeof chartAny.setScrollEnabled === "function") {
      chartAny.setScrollEnabled(true);
    }
    if (typeof chartAny.setZoomEnabled === "function") {
      chartAny.setZoomEnabled(true);
    }
    applyTimeframeDateFormat(
      chart,
      useChartStore3.getState().slots[chartPanelId].selectedTimeframe
    );

    if (typeof chartAny.setMaxOffsetRightDistance === "function") {
      chartAny.setMaxOffsetRightDistance(1_000_000);
    }
    if (typeof chartAny.setOffsetRightDistance === "function") {
      chartAny.setOffsetRightDistance(120);
    }

    // MT5-style axis drag scaling (SR Global request 2026-09-08). klinecharts v9 already scales the
    // price range on MOUSE drag of the y-axis (and time-zooms on x-axis drag) when the pane's
    // axisOptions.scrollZoomEnabled is on — assert it explicitly. Its TOUCH handlers, however, only
    // hit-test overlays and never run that math, so on iOS/Android the axes are inert: replicate the
    // library's own mouse math on touch for the y-axis (price range) and x-axis (time zoom).
    if (typeof chartAny.setPaneOptions === "function") {
      try { chartAny.setPaneOptions({ id: "candle_pane", axisOptions: { scrollZoomEnabled: true } }); } catch { /* older lib */ }
    }
    try {
      const panes: any[] = typeof chartAny.getAllDrawPanes === "function" ? chartAny.getAllDrawPanes() : [];
      for (const pane of panes) {
        const yWidget = pane?.getYAxisWidget?.();
        const yEl: HTMLElement | undefined = yWidget?.getContainer?.();
        if (yEl) {
          let start: { y: number; range: any } | null = null;
          yEl.style.touchAction = "none";
          yEl.addEventListener("touchstart", (e: TouchEvent) => {
            if (e.touches.length !== 1) { start = null; return; }
            const axis = pane.getAxisComponent?.();
            const range = axis?.getRange?.();
            start = range ? { y: e.touches[0].pageY, range: { ...range } } : null;
          }, { passive: true });
          yEl.addEventListener("touchmove", (e: TouchEvent) => {
            if (!start || e.touches.length !== 1) return;
            const axis = pane.getAxisComponent?.();
            if (!axis || (axis.getScrollZoomEnabled && !axis.getScrollZoomEnabled())) return;
            const scale = e.touches[0].pageY / start.y;
            if (!Number.isFinite(scale) || scale <= 0) return;
            const { from, to, range } = start.range;
            const newRange = range * scale;
            const dif = (newRange - range) / 2;
            const newFrom = from - dif;
            const newTo = to + dif;
            const realFrom = axis.convertToRealValue(newFrom);
            const realTo = axis.convertToRealValue(newTo);
            axis.setRange({ from: newFrom, to: newTo, range: newRange, realFrom, realTo, realRange: realTo - realFrom });
            chartAny.adjustPaneViewport?.(false, true, true, true);
            e.preventDefault();
          }, { passive: false });
          yEl.addEventListener("touchend", () => { start = null; }, { passive: true });
          // Double-tap the price axis → back to auto-scale (mirrors the library's double-click).
          let lastTap = 0;
          yEl.addEventListener("touchend", () => {
            const now = Date.now();
            if (now - lastTap < 300) { pane.getAxisComponent?.()?.setAutoCalcTickFlag?.(true); chartAny.adjustPaneViewport?.(false, true, true, true); }
            lastTap = now;
          }, { passive: true });
        }
      }
      // x-axis: drag horizontally to zoom time (public zoomAtCoordinate keeps this off the internals).
      const xEl: HTMLElement | undefined = chartAny.getXAxisPane?.()?.getMainWidget?.()?.getContainer?.()
        ?? chartAny.getXAxisPane?.()?.getContainer?.();
      if (xEl && typeof chartAny.zoomAtCoordinate === "function") {
        let xStart: { x: number; scale: number } | null = null;
        xEl.style.touchAction = "none";
        xEl.addEventListener("touchstart", (e: TouchEvent) => {
          xStart = e.touches.length === 1 ? { x: e.touches[0].pageX, scale: 1 } : null;
        }, { passive: true });
        xEl.addEventListener("touchmove", (e: TouchEvent) => {
          if (!xStart || e.touches.length !== 1) return;
          const scale = xStart.x / e.touches[0].pageX;
          if (!Number.isFinite(scale)) return;
          const zoomScale = (scale - xStart.scale) * 10;
          xStart.scale = scale;
          chartAny.zoomAtCoordinate(zoomScale, { x: xStart.x, y: 0 });
          e.preventDefault();
        }, { passive: false });
        xEl.addEventListener("touchend", () => { xStart = null; }, { passive: true });
      }
    } catch { /* axis internals unavailable → mouse drag still works, touch just no-ops */ }

    dataLoadStateRef.current.isInitialized = false;
    dataLoadStateRef.current.newestTimestamp = 0;

    const initSymbolCurrency = selectedSymbol.currency;

    // Token captured for this chart instance. If the chart is disposed
    // (symbol/timeframe change) before a `getMoreData` Promise resolves, we
    // detect it via `chartInstanceRef.current !== chart` and refuse to invoke
    // the stale callback, avoiding wrong-data flashes and stuck pendings.
    const chartTokenLocal = chart;

    // Yield to the browser before invoking the klinecharts callback so the
    // current scroll/pan frame has a chance to paint before klinecharts
    // synchronously merges the older bars and re-lays out the chart.
    const yieldAndCallback = (
      cb: (data: any[], more?: boolean) => void,
      data: any[],
      more: boolean
    ) => {
      const run = () => {
        if (chartInstanceRef.current !== chartTokenLocal) return;
        cb(data, more);
      };
      if (
        typeof window !== "undefined" &&
        window.requestAnimationFrame
      ) {
        window.requestAnimationFrame(run);
      } else {
        run();
      }
    };

    // Throttle scroll-back requests:
    //   - Only one in-flight getMoreData per chart instance. While a request is
    //     in flight, klinecharts may keep asking on continued user scroll.
    //     We answer those repeat asks immediately with an empty payload + more=true
    //     so klinecharts stops blocking the scroll thread, then trigger one
    //     follow-up request once the in-flight one resolves.
    //   - When the server returns no older bars, pass more=false so klinecharts
    //     stops asking on subsequent scroll attempts.
    let scrollLoadInflight = false;
    let scrollLoadAtHistoryEnd = false;
    let pendingForwardParams: { timestamp: number; callback: any } | null = null;

    const requestOlderBars = (timestamp: number, callback: any) => {
      if (chartInstanceRef.current !== chartTokenLocal) return;
      if (scrollLoadAtHistoryEnd) {
        callback([], false);
        return;
      }
      if (scrollLoadInflight) {
        // Coalesce repeated asks during a single scroll burst.
        pendingForwardParams = { timestamp, callback };
        callback([], true);
        return;
      }
      scrollLoadInflight = true;

      useChartStore3
        .getState()
        .getMoreData(timestamp, chartPanelId)
        .then((olderData) => {
          scrollLoadInflight = false;
          if (chartInstanceRef.current !== chartTokenLocal) return;
          const liveCurrency =
            useChartStore3.getState().slots[chartPanelId].selectedSymbol
              ?.currency;
          if (
            !liveCurrency ||
            liveCurrency.trim().toUpperCase() !==
              initSymbolCurrency.trim().toUpperCase()
          ) {
            return;
          }
          const hasMore = Array.isArray(olderData) && olderData.length > 0;
          if (!hasMore) scrollLoadAtHistoryEnd = true;
          yieldAndCallback(callback, olderData as any, hasMore);

          if (pendingForwardParams) {
            const next = pendingForwardParams;
            pendingForwardParams = null;
            requestOlderBars(next.timestamp, next.callback);
          }
        })
        .catch((error) => {
          scrollLoadInflight = false;
          pendingForwardParams = null;
          const reason =
            error && typeof error.message === "string" ? error.message : "";
          if (reason === "Symbol changed" || reason === "Timeframe changed") {
            return;
          }
          console.error("Failed to load more data:", error);
          yieldAndCallback(callback, [], false);
        });
    };

    (chart as any).setLoadDataCallback((params: any) => {
      if (
        params.type === LoadDataType.Forward ||
        params.type === "forward" ||
        params.type === 1
      ) {
        if (!params.data) {
          return;
        }
        const timestamp = params?.data?.timestamp;
        requestOlderBars(timestamp, params.callback);
      } else if (
        params.type === LoadDataType.Backward ||
        params.type === "backward" ||
        params.type === 2
      ) {
        params.callback([], false);
      } else if (
        params.type === LoadDataType.Init ||
        params.type === "init" ||
        params.type === 0
      ) {
        params.callback([], false);
      }
    });

    if (!dataLoadStateRef.current.isInitialized) {
      useChartStore3
        .getState()
        .getInitialData(chartPanelId)
        .then((initialData) => {
          applyInitialDataIfCurrent(
            chart as unknown as ChartIndicatorApi,
            chartTokenLocal,
            initSymbolCurrency,
            initialData
          );
        })
        .catch((error) => {
          const reason =
            error && typeof error.message === "string" ? error.message : "";
          if (reason === "Symbol changed" || reason === "Timeframe changed") {
            return;
          }
          console.error("Failed to load initial data:", error);
        });
    }

    // ResizeObserver.
    // RP-14: debounce with a single tracked timeout that captures THIS chart
    // instance (`chartTokenLocal`) rather than reading `chartInstanceRef.current`
    // (which may point at a newer instance by the time the timeout fires). The
    // timeout is cleared on cleanup so it never resizes a disposed chart.
    let resizeTimeoutId: number | null = null;
    const resizeObserver = new ResizeObserver(() => {
      if (resizeTimeoutId !== null) window.clearTimeout(resizeTimeoutId);
      resizeTimeoutId = window.setTimeout(() => {
        resizeTimeoutId = null;
        if (chartInstanceRef.current !== chartTokenLocal) return;
        chartTokenLocal.resize();
      }, 10) as unknown as number;
    });
    resizeObserver.observe(chartRef.current);

    // Indicator effects may run before this chart exists; apply store state now.
    clearIndicatorSyncState(mainIndicatorSyncRef.current);
    clearIndicatorSyncState(separateIndicatorSyncRef.current);
    syncIndicators(
      chart as unknown as ChartIndicatorApi,
      { isOverlay: true },
      (useMainChartStore.getState().mainChart.indicators ??
        []) as IndicatorSyncItem[],
      mainIndicatorSyncRef.current
    );
    syncIndicators(
      chart as unknown as ChartIndicatorApi,
      { isOverlay: false },
      (useChartIndicatorsStore.getState().indicators.chartIndicators ??
        []) as IndicatorSyncItem[],
      separateIndicatorSyncRef.current
    );

    // Cleanup
    return () => {
      resizeObserver.disconnect();
      if (resizeTimeoutId !== null) {
        window.clearTimeout(resizeTimeoutId);
        resizeTimeoutId = null;
      }
      clearIndicatorSyncState(mainIndicatorSyncRef.current);
      clearIndicatorSyncState(separateIndicatorSyncRef.current);
      unregisterChartPanel(chartPanelId);
      if (chartRef.current && chartInstanceRef.current) {
        klinecharts.dispose(chartRef.current);
        chartInstanceRef.current = null;
      }
    };
  }, [
    // RP-03: `selectedTimeframe` intentionally NOT a dep — a TF change must not
    // dispose+recreate the chart (which loses zoom/scroll and rebuilds
    // indicators/overlays/drawings). The dedicated TF-reload effect below reloads
    // data on the existing instance instead.
    selectedSymbol?.currency,
    selectedSymbol?.digits,
    chartPanelId,
    applyInitialDataIfCurrent,
  ]);

  // RP-03: on timeframe change, reload data on the EXISTING chart instance
  // instead of tearing it down. Reset the data-load state and apply fresh OHLC;
  // klinecharts keeps indicators/overlays/drawings and (visible-range aside)
  // the user's zoom/scroll. The load-more callback already reads the current TF
  // from the store, so scroll-back continues to work after the switch.
  const isFirstTfEffectRef = useRef(true);
  useEffect(() => {
    // Skip the very first run for a freshly created chart — the init effect
    // already loaded initial data. Only react to genuine TF switches.
    if (isFirstTfEffectRef.current) {
      isFirstTfEffectRef.current = false;
      return;
    }
    const chart = chartInstanceRef.current;
    if (!chart || !selectedSymbol) return;

    const expectedCurrency = selectedSymbol.currency;
    const chartToken = chart;

    dataLoadStateRef.current.isInitialized = false;
    dataLoadStateRef.current.newestTimestamp = 0;

    let cancelled = false;
    useChartStore3
      .getState()
      .getInitialData(chartPanelId)
      .then((initialData) => {
        if (cancelled) return;
        applyInitialDataIfCurrent(
          chart as unknown as ChartIndicatorApi,
          chartToken,
          expectedCurrency,
          initialData
        );
      })
      .catch((error) => {
        const reason =
          error && typeof error.message === "string" ? error.message : "";
        if (reason === "Symbol changed" || reason === "Timeframe changed") {
          return;
        }
        console.error("Failed to load data on timeframe change:", error);
      });

    return () => {
      cancelled = true;
    };
    // Symbol changes are handled by the init effect (fresh chart); this effect
    // is purely for TF switches on an existing instance.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTimeframe]);

  // Invalidate stale WASM indicator render maps and reset load state on symbol change.
  useEffect(() => {
    dataLoadStateRef.current.isInitialized = false;
    dataLoadStateRef.current.newestTimestamp = 0;
    for (const instanceId of mainIndicatorSyncRef.current.keys()) {
      clearCustomComputeCache(instanceId);
    }
    for (const instanceId of separateIndicatorSyncRef.current.keys()) {
      clearCustomComputeCache(instanceId);
    }
  }, [selectedSymbol?.currency, chartPanelId]);

  // When IndexedDB hydrate or processBatch fills `ohlcData` after chart init, apply once (init effect only depends on symbol/TF).
  useEffect(() => {
    const chart = chartInstanceRef.current;
    if (!chart || !selectedSymbol) return;

    const expectedCurrency = selectedSymbol.currency;
    const chartToken = chart;

    const list = chart.getDataList?.() as unknown[] | undefined;
    if (list && list.length > 0) return;

    let cancelled = false;
    useChartStore3
      .getState()
      .getInitialData(chartPanelId)
      .then((initialData) => {
        if (cancelled) return;
        applyInitialDataIfCurrent(
          chart as unknown as ChartIndicatorApi,
          chartToken,
          expectedCurrency,
          initialData
        );
      })
      .catch((error) => {
        const reason =
          error && typeof error.message === "string" ? error.message : "";
        if (reason === "Symbol changed" || reason === "Timeframe changed") {
          return;
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    ohlcRevision,
    selectedSymbol?.currency,
    selectedTimeframe,
    chartPanelId,
    applyInitialDataIfCurrent,
  ]);

  useEffect(() => {
    if (!chartInstanceRef.current) return;
    // Re-apply styles WITHOUT the axis blocks: klinecharts' setStyles resets the candle pane to
    // auto-scale whenever `yAxis.type` is present, which silently threw away a user's manual
    // price-axis scale on every crosshair toggle / chart-type switch. The axes are configured once
    // at init; only the visual style keys need to follow chartStyles.
    const { yAxis: _y, xAxis: _x, ...visual } = chartStyles as any;
    chartInstanceRef.current.setStyles(visual);
  }, [chartStyles]);

  useEffect(() => {
    lastLiveCandleApplyRef.current = 0;
  }, [selectedTimeframe]);

  // Keep the x-axis tick date format in sync with the selected timeframe.
  useEffect(() => {
    if (!chartInstanceRef.current) return;
    applyTimeframeDateFormat(chartInstanceRef.current, selectedTimeframe);
  }, [selectedTimeframe]);

  // Crosshair mode no longer disables navigation — scroll and zoom stay
  // enabled while the crosshair is visible (FIX G). Re-assert enabled state in
  // case anything else toggled it.
  useEffect(() => {
    if (!chartInstanceRef.current) return;

    const chart = chartInstanceRef.current as any;
    if (typeof chart.setScrollEnabled === "function") {
      chart.setScrollEnabled(true);
    }
    if (typeof chart.setZoomEnabled === "function") {
      chart.setZoomEnabled(true);
    }
  }, [isCrosshairVisible]);

  // Live candle update. We coalesce updates with rAF so a burst of quote ticks
  // produces at most one klinecharts updateData per frame (~60fps). Without
  // this, every quote tick re-ran getDataList + updateData synchronously,
  // starving pan/zoom of frame budget.
  //
  // We deliberately read `selectedTimeframe` from the store inside the rAF
  // callback (instead of closing over the effect-time value) so a pending
  // frame uses the LATEST timeframe even if the user switched TF between
  // scheduling and flushing.
  const liveCandleFrameRef = useRef<number | null>(null);
  // Whether liveCandleFrameRef holds a rAF handle or a setTimeout handle
  // (coarse-TF throttled flushes are scheduled with setTimeout).
  const liveCandleFrameIsTimeoutRef = useRef(false);
  const liveCandlePendingRef = useRef<typeof currentQuote>(null);
  // Throttle for mirroring live ticks into the store's base 1m series (CD-08).
  const lastLiveStoreMergeRef = useRef(0);
  const cancelLiveCandleFlush = () => {
    if (liveCandleFrameRef.current === null) return;
    if (liveCandleFrameIsTimeoutRef.current) {
      window.clearTimeout(liveCandleFrameRef.current);
    } else if (
      typeof window !== "undefined" &&
      window.cancelAnimationFrame
    ) {
      window.cancelAnimationFrame(liveCandleFrameRef.current);
    } else {
      window.clearTimeout(liveCandleFrameRef.current);
    }
    liveCandleFrameRef.current = null;
    liveCandleFrameIsTimeoutRef.current = false;
  };
  // Cancel any pending live-candle rAF when symbol/TF changes - the new chart
  // would otherwise inherit a stale flush against the old chart instance.
  useEffect(() => {
    cancelLiveCandleFlush();
    liveCandlePendingRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSymbol?.currency, selectedTimeframe, chartPanelId]);

  // Proposal 65: apply a resync-backfilled tail to the live chart via the same
  // apply path the init/TF-reload effects use (preserves the current instance +
  // symbol guards).
  const applyResyncedData = useCallback(
    (bars: Array<{ timestamp: number }>): boolean => {
      const chart = chartInstanceRef.current;
      const currency =
        useChartStore3.getState().slots[chartPanelId].selectedSymbol?.currency;
      if (!chart || !currency || bars.length === 0) return false;
      return applyInitialDataIfCurrent(
        chart as unknown as ChartIndicatorApi,
        chart,
        currency,
        bars,
      );
    },
    [chartPanelId, applyInitialDataIfCurrent],
  );

  // Proposal 65: the per-panel visibility resync gate. Freezes the pump on hide,
  // backfills the missing range on wake (sequenced behind the socket recycle),
  // then re-arms — so a live tick can never paint across an un-backfilled gap.
  useChartVisibilityGate({
    chartPanelId,
    chartInstanceRef,
    cancelLiveCandleFlush,
    liveCandlePendingRef,
    applyResyncedData,
  });

  // Refs mirroring values the flush needs to read; kept current by the effects
  // below so the imperative quote subscription can schedule a flush without
  // capturing stale closures.
  const showAskPriceLineRef = useRef(showAskPriceLine);
  const askBidDigitsRef = useRef<number>(selectedSymbol?.digits ?? 5);
  useEffect(() => {
    showAskPriceLineRef.current = showAskPriceLine;
  }, [showAskPriceLine]);
  useEffect(() => {
    askBidDigitsRef.current = selectedSymbol?.digits ?? 5;
  }, [selectedSymbol?.digits]);

  // RP-05: move the existing ask/bid overlays and refresh their labels. Called
  // once per rAF flush (previously ran unthrottled twice per tick).
  const applyAskBidOverlay = useCallback(() => {
    const chart = chartInstanceRef.current;
    const quote = currentQuoteRef.current;
    if (!chart || !showAskPriceLineRef.current || !quote) return;
    const digits = askBidDigitsRef.current;
    const { ask, bid } = quote;
    try {
      if (askLineIdRef.current && ask) {
        chart.overrideOverlay({
          id: askLineIdRef.current,
          points: [{ value: ask }],
          extendData: { text: `ASK ${ask.toFixed(digits)}`, priceType: "ask" },
        });
      }
      if (bidLineIdRef.current && bid) {
        chart.overrideOverlay({
          id: bidLineIdRef.current,
          points: [{ value: bid }],
          extendData: { text: `BID ${bid.toFixed(digits)}`, priceType: "bid" },
        });
      }
    } catch (error) {
      // Overlay may have been disposed between ticks; ignore.
    }
  }, []);

  // Core per-frame flush: reads the latest quote from refs and updates the
  // klinecharts live candle + ask/bid overlays. Extracted from the old
  // `currentQuote` effect so it can be driven imperatively by the quote
  // subscription (RP-01) without per-tick React renders.
  const flushLiveCandleImpl = useCallback(() => {
      liveCandleFrameRef.current = null;
      liveCandleFrameIsTimeoutRef.current = false;
      lastLiveCandleApplyRef.current = Date.now();
      // RP-05: fold ask/bid overlay updates into the coalesced flush.
      applyAskBidOverlay();
      const quote = liveCandlePendingRef.current;
      const chart = chartInstanceRef.current;
      if (!chart || !quote) return;

      const liveSymbol =
        useChartStore3.getState().slots[chartPanelId].selectedSymbol?.currency;
      if (
        !liveSymbol ||
        !quote.symbol ||
        quote.symbol.trim().toUpperCase() !== liveSymbol.trim().toUpperCase()
      ) {
        return;
      }

      // CD-09: paint the live candle from the SAME price side the engine builds
      // its 1m bars on (last, with mid/bid fallbacks). Painting `quote.bid` here
      // meant every server OHLC batch snapped the last candle by the bid/last
      // offset while the ASK/BID overlay lines kept tracking the quote — the
      // "candles move independently of bid/ask" report.
      const price = pickLiveCandlePrice(quote);
      const quoteTime = quote.time;
      const quoteVolume = quote.volume;
      if (!price || !quoteTime) return;

      const tf =
        useChartStore3.getState().slots[chartPanelId].selectedTimeframe;
      // Proposal 65: the visibility resync gate owns the tail while the panel is
      // stale/resyncing. Read it fresh each flush so a frozen-then-fired rAF on
      // wake can't advance the series across the un-backfilled gap.
      const chartFresh =
        useChartStore3.getState().slots[chartPanelId].chartFreshness;
      const candleTimestamp = getCandleTimestamp(quoteTime, tf);
      const currentData = chart.getDataList();
      if (!currentData || currentData.length === 0) return;

      const lastCandle = currentData[currentData.length - 1];
      const lastCandleTimestamp = lastCandle.timestamp as number;

      if (candleTimestamp === lastCandleTimestamp) {
        // CD-07: leave accumulated candle volume untouched — the quote's
        // `volume` is an instantaneous per-tick value, not a candle total.
        // Server OHLC batches deliver the authoritative volume.
        chart.updateData({
          ...lastCandle,
          high: Math.max(lastCandle.high as number, price),
          low: Math.min(lastCandle.low as number, price),
          close: price,
        });
      } else if (candleTimestamp > lastCandleTimestamp) {
        // Proposal 65 — GATE INVARIANT: a live tick may never advance the series
        // across an un-backfilled gap. While the panel is stale/resyncing the
        // visibility gate is backfilling [lastGoodBarTs, now]; drop this tick so
        // it can't strand a lone "now" candle next to a minutes-old bar.
        if (chartFresh !== "live") return;
        chart.updateData({
          timestamp: candleTimestamp,
          open: price,
          high: price,
          low: price,
          close: price,
          volume: quoteVolume || 0,
        });
      }

      // CD-08: mirror the live tick into the store's base 1m series so a
      // timeframe switch re-aggregates from fresh data instead of snapping
      // back to the last server batch. Throttled to at most once per second.
      // We intentionally do NOT bump ohlcRevision: revision-driven effects
      // (chart re-init, indicator preview recompute) must not churn per tick;
      // consumers read slot.ohlcData imperatively on TF switch.
      const nowMs = Date.now();
      if (nowMs - lastLiveStoreMergeRef.current >= 1000) {
        lastLiveStoreMergeRef.current = nowMs;
        const minuteTs = getCandleTimestamp(quoteTime, "1m");
        useChartStore3.setState((root) => {
          const slot = root.slots[chartPanelId];
          const slotSym = slot.selectedSymbol?.currency;
          if (
            !slotSym ||
            slotSym.trim().toUpperCase() !==
              quote.symbol!.trim().toUpperCase()
          ) {
            return root;
          }
          const series = slot.ohlcData;
          if (!series || series.length === 0) return root;
          const last = series[series.length - 1];
          if (minuteTs < last.timestamp) return root;
          let next: typeof series;
          if (minuteTs === last.timestamp) {
            const high = Math.max(last.high, price);
            const low = Math.min(last.low, price);
            if (high === last.high && low === last.low && last.close === price) {
              return root;
            }
            next = series.slice(0, -1);
            next.push({ ...last, high, low, close: price });
          } else {
            // Proposal 65: same gate invariant for the base 1m mirror — never
            // append a fresh minute bar across a gap while resyncing.
            if (chartFresh !== "live") return root;
            next = series.concat({
              timestamp: minuteTs,
              open: price,
              high: price,
              low: price,
              close: price,
              volume: 0,
            });
          }
          return {
            slots: {
              ...root.slots,
              [chartPanelId]: { ...slot, ohlcData: next },
            },
          };
        });
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartPanelId, applyAskBidOverlay]);

  // Keep a stable ref to the flush so the imperative quote subscription can call
  // the latest implementation without re-subscribing on every render.
  const flushLiveCandleRef = useRef(flushLiveCandleImpl);
  useEffect(() => {
    flushLiveCandleRef.current = flushLiveCandleImpl;
  }, [flushLiveCandleImpl]);

  // Schedule one coalesced flush (rAF, or a delayed setTimeout on coarse TFs).
  // Idempotent: if a flush is already pending, the new tick was already stored
  // in liveCandlePendingRef and will be picked up by the pending flush.
  const scheduleLiveFlush = useCallback(() => {
    if (liveCandleFrameRef.current !== null) return;

    const tf = useChartStore3.getState().slots[chartPanelId].selectedTimeframe;
    const coarseTf = tf === "1d" || tf === "1w" || tf === "1mn";
    let flushDelayMs = 0;
    if (coarseTf) {
      const remaining = 2000 - (Date.now() - lastLiveCandleApplyRef.current);
      if (remaining > 0) flushDelayMs = remaining;
    }

    const run = () => flushLiveCandleRef.current();
    if (flushDelayMs > 0) {
      liveCandleFrameIsTimeoutRef.current = true;
      liveCandleFrameRef.current = window.setTimeout(
        run,
        flushDelayMs,
      ) as unknown as number;
    } else {
      liveCandleFrameIsTimeoutRef.current = false;
      liveCandleFrameRef.current =
        typeof window !== "undefined" && window.requestAnimationFrame
          ? window.requestAnimationFrame(run)
          : (window.setTimeout(run, 16) as unknown as number);
    }
  }, [chartPanelId]);

  // RP-01: subscribe imperatively to the subscribed-symbols store. Every quote
  // tick feeds refs + schedules the coalesced flush WITHOUT setting React state.
  // The only React state updated is `displayQuote` (for children that render the
  // quote), and only when bid/ask actually change, throttled to one rAF.
  const displayQuoteFrameRef = useRef<number | null>(null);
  useEffect(() => {
    const currency = selectedSymbol?.currency;

    const ingest = (quote: LiveQuote | null | undefined) => {
      const q = quote ?? null;
      currentQuoteRef.current = q;
      bidCountdownLatestBidRef.current = q?.bid ?? 0;

      if (q && q.bid && q.ask && q.time) {
        // CD-06: always store the latest quote so coarse-TF throttling
        // coalesces ticks instead of dropping them.
        liveCandlePendingRef.current = q;
        if (chartInstanceRef.current) scheduleLiveFlush();
      }

      // Throttle the React-rendered snapshot to one rAF; only push when
      // bid/ask changed so children don't re-render on identical ticks.
      if (displayQuoteFrameRef.current === null) {
        const push = () => {
          displayQuoteFrameRef.current = null;
          const latest = currentQuoteRef.current;
          setDisplayQuote((prev) => {
            if (
              prev?.bid === latest?.bid &&
              prev?.ask === latest?.ask &&
              prev?.time === latest?.time
            ) {
              return prev;
            }
            return latest;
          });
        };
        displayQuoteFrameRef.current =
          typeof window !== "undefined" && window.requestAnimationFrame
            ? window.requestAnimationFrame(push)
            : (window.setTimeout(push, 16) as unknown as number);
      }
    };

    // Seed with the current value immediately (covers symbol switches).
    ingest(
      currency
        ? useSubscribedSymbolsStore.getState().byCurrency.get(currency)?.quote
        : null
    );

    if (!currency) {
      return () => {
        if (displayQuoteFrameRef.current !== null) {
          if (typeof window !== "undefined" && window.cancelAnimationFrame) {
            window.cancelAnimationFrame(displayQuoteFrameRef.current);
          } else {
            window.clearTimeout(displayQuoteFrameRef.current);
          }
          displayQuoteFrameRef.current = null;
        }
      };
    }

    let prevQuote =
      useSubscribedSymbolsStore.getState().byCurrency.get(currency)?.quote;
    const unsubscribe = useSubscribedSymbolsStore.subscribe((state) => {
      const nextQuote = state.byCurrency.get(currency)?.quote;
      if (nextQuote === prevQuote) return;
      prevQuote = nextQuote;
      ingest(nextQuote);
    });

    return () => {
      unsubscribe();
      if (displayQuoteFrameRef.current !== null) {
        if (typeof window !== "undefined" && window.cancelAnimationFrame) {
          window.cancelAnimationFrame(displayQuoteFrameRef.current);
        } else {
          window.clearTimeout(displayQuoteFrameRef.current);
        }
        displayQuoteFrameRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSymbol?.currency, scheduleLiveFlush]);

  useEffect(() => {
    return () => {
      cancelLiveCandleFlush();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useChartDrawings({
    chartInstanceRef,
    selectedDrawingType: selectedObjectType,
    selectedSymbol,
    selectedTimeframe,
    chartPanelId: String(chartPanelId),
    drawings: objects,
    addDrawing: addChartObject,
    updateDrawingSettings: updateChartObjectSettings,
    getDrawingsBySymbol: getObjectsBySymbol,
    setShowTextModal: openTextModal,
    setTextValue,
    onDrawingAdded: onObjectAdded,
    dataRevision: chartDataNonce,
  });

  // MT5-style trade overlay: entries, pending orders, and static SL/TP levels.
  const { forceCleanupTrade } = useChartTradeOverlay({
    chartInstanceRef,
    selectedSymbol,
    selectedTimeframe,
    trades: allTrades,
    selectedTradeTicket,
    currentQuote,
    showTradePositions,
    showPendingOrders: showTradeOrders,
    showSlTpLevels,
    isPipsVisible,
    onTradeClick: onTradeLineClick,
    onTradeDoubleClick: onTradeLineDoubleClick,
    onTradeClose: onTradeLineClose,
    onPendingEntryPriceChange,
    onTpClose: onTpLineClose,
    onSlClose: onSlLineClose,
    onTpPriceChange: onTpLinePriceChange,
    onSlPriceChange: onSlLinePriceChange,
    onTradeInteraction: handleTradeOverlayInteraction,
    readOnly: isInvestor,
  });

  // Add trade history lines
  useTradeHistoryLines({
    chartInstanceRef,
    selectedSymbol,
    selectedTimeframe,
    showTradeHistory,
  });

  // Main chart overlay indicators — incremental diff sync (no full pane wipe)
  const mainWasmTickRef = useRef(customWasmComputeTick);
  useEffect(() => {
    const chart = chartInstanceRef.current;
    if (!chart) return;

    const changed = syncIndicators(
      chart as unknown as ChartIndicatorApi,
      { isOverlay: true },
      (mainChartIndicators ?? []) as IndicatorSyncItem[],
      mainIndicatorSyncRef.current
    );
    // Custom WASM compute results arrive out-of-band (tick bump), so they
    // still need an explicit recalc even when the diff sync saw no changes.
    const wasmTickChanged = mainWasmTickRef.current !== customWasmComputeTick;
    mainWasmTickRef.current = customWasmComputeTick;
    if (changed || wasmTickChanged) {
      forceChartIndicatorRecalc(chart as unknown as ChartIndicatorApi);
    }
  }, [
    mainChartIndicators,
    selectedSymbol?.currency,
    selectedTimeframe,
    customWasmComputeTick,
    customCompileRevisionKey,
  ]);

  // Separate useEffect for volume management.
  // RP-08: keyed on `selectedSymbol?.currency` (+ TF) instead of the whole
  // `selectedSymbol` object so a new object identity with the same symbol does
  // not needlessly re-run the sweep. When the chart was just created there is no
  // VOL indicator to remove, so the remove-all sweep is skipped.
  useEffect(() => {
    if (!chartInstanceRef.current) return;

    const wasFreshlyCreated = chartFreshlyCreatedRef.current;
    chartFreshlyCreatedRef.current = false;

    // Remove all existing VOL indicators from all panes (skip on a fresh chart).
    if (!wasFreshlyCreated) {
      const allIndicators = chartInstanceRef.current.getIndicatorByPaneId();
      if (allIndicators && allIndicators instanceof Map) {
        allIndicators.forEach((indicatorsInPane, paneId) => {
          if (
            indicatorsInPane &&
            indicatorsInPane instanceof Map &&
            indicatorsInPane.has("VOL")
          ) {
            try {
              chartInstanceRef.current!.removeIndicator(paneId, "VOL");
            } catch (error) {
              console.error(`Failed to remove VOL from pane ${paneId}:`, error);
            }
          }
        });
      }
    }

    // Add volume indicator to main pane if enabled
    if (showVolumes) {
      try {
        chartInstanceRef.current!.createIndicator("VOL", true);
      } catch (error) {
        console.error("Failed to create volume indicator in main pane:", error);
      }
    }
  }, [showVolumes, selectedSymbol?.currency, selectedTimeframe]);

  // RP-12: removed the `debugAllOverlays` empty-body loop and the
  // `window.debugChart` global debug hook that were shipped to production.

  // Monitor ALL trades - cleanup lines when ANY trade disappears
  const previousTradesRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    // Create set of current trade tickets
    const currentTradeTickets = new Set(
      allTrades.map((t) => t.ticket.toString())
    );
    const previousTradeTickets = previousTradesRef.current;

    // Find trades that disappeared
    const disappearedTrades = [...previousTradeTickets].filter(
      (ticket) => !currentTradeTickets.has(ticket)
    );

    // Cleanup lines for disappeared trades
    disappearedTrades.forEach((ticket) => {
      // Force cleanup of simple SL/TP lines
      forceCleanupTrade(ticket);

      // Aggressive cleanup of ALL overlays for this trade
      if (chartInstanceRef.current) {
        try {
          const overlayMap = chartInstanceRef.current.getOverlaysByPaneId?.();
          if (overlayMap && overlayMap instanceof Map) {
            overlayMap.forEach((paneOverlays, paneId) => {
              if (paneOverlays instanceof Map) {
                paneOverlays.forEach((overlay, overlayId) => {
                  if (
                    overlay &&
                    overlay.extendData &&
                    overlay.extendData.ticket === ticket
                  ) {
                    try {
                      chartInstanceRef.current.removeOverlay(overlayId);
                    } catch (error) {
                      console.error("Failed to remove overlay:", error);
                    }
                  }
                });
              }
            });
          }
        } catch (error) {
          console.error("Error during global trade cleanup:", error);
        }
      }
    });

    // Update previous trades reference
    previousTradesRef.current = currentTradeTickets;
  }, [allTrades, forceCleanupTrade]);

  // Close the selected trade controls when the trade disappears from the store.
  useEffect(() => {
    if (!selectedTradeTicket) {
      return;
    }

    const tradeStillExists = allTrades.some(
      (trade) => trade.ticket.toString() === selectedTradeTicket
    );

    if (!tradeStillExists) {
      forceCleanupTrade(selectedTradeTicket);
      setSelectedTradeTicket(null);
    }
  }, [allTrades, selectedTradeTicket, forceCleanupTrade]);

  useEffect(() => {
    const currency = selectedSymbol?.currency ?? null;
    const st = useChartStore3.getState();
    const pending = st.pendingTradeChartFocus;

    if (pending && pending.panelId === chartPanelId) {
      if (pending.symbolCurrency === currency) {
        setSelectedTradeTicket(pending.ticket);
        const hints: TradeOverlayPlacementHints | null =
          pending.activateSlPlacement || pending.activateTpPlacement
            ? {
                activateSl: pending.activateSlPlacement,
                activateTp: pending.activateTpPlacement,
              }
            : null;
        setTradeOverlayPlacementHints(hints);
        st.clearPendingTradeChartFocus();
        tradeChartPrevCurrencyRef.current = currency;
        return;
      }
      setSelectedTradeTicket(null);
      return;
    }

    if (pending && pending.panelId !== chartPanelId) {
      setSelectedTradeTicket(null);
      tradeChartPrevCurrencyRef.current = currency;
      return;
    }

    const prev = tradeChartPrevCurrencyRef.current;
    tradeChartPrevCurrencyRef.current = currency;
    if (prev !== currency) {
      setSelectedTradeTicket(null);
    }
  }, [selectedSymbol?.currency, chartPanelId, tradeFocusGeneration]);

  useEffect(() => {
    if (!selectedTradeTicket) {
      setTradeOverlayPlacementHints(null);
    }
  }, [selectedTradeTicket]);

  // Lifecycle effect for ask/bid price lines.
  //
  // Previously these overlays were torn down and recreated with a fresh random
  // id on every quote tick, which made klinecharts re-register and repaint two
  // overlays per tick. Now we create them once (with stable ids) when the
  // feature/symbol/colors change, and let the tick-update effect below mutate
  // them in place via `overrideOverlay`.
  useEffect(() => {
    const chart = chartInstanceRef.current;
    if (!chart) {
      return;
    }

    // Feature disabled - make sure any existing overlays are removed.
    if (!showAskPriceLine) {
      if (askLineIdRef.current) {
        try {
          chart.removeOverlay(askLineIdRef.current);
        } catch (error) {
          // Line might not exist, ignore error
        }
        askLineIdRef.current = null;
      }
      if (bidLineIdRef.current) {
        try {
          chart.removeOverlay(bidLineIdRef.current);
        } catch (error) {
          // Line might not exist, ignore error
        }
        bidLineIdRef.current = null;
      }
      return;
    }

    const digits = selectedSymbol?.digits ?? 5;
    const initialAsk = currentQuoteRef.current?.ask ?? 0;
    const initialBid = currentQuoteRef.current?.bid ?? 0;

    try {
      askLineIdRef.current = chart.createOverlay({
        name: "horizontalStraightLine",
        id: "ask_price_line",
        points: [{ value: initialAsk }],
        styles: {
          line: {
            color: askLineColor,
            size: 1,
            style: "dashed",
          },
          text: {
            color: askLineColor,
            size: 11,
            backgroundColor: `${askLineColor}20`,
            borderColor: askLineColor,
            borderSize: 1,
            paddingLeft: 4,
            paddingRight: 4,
            paddingTop: 2,
            paddingBottom: 2,
            borderRadius: 2,
          },
        },
        extendData: {
          text: initialAsk
            ? `ASK ${initialAsk.toFixed(digits)}`
            : "",
          priceType: "ask",
        },
      });

      bidLineIdRef.current = chart.createOverlay({
        name: "horizontalStraightLine",
        id: "bid_price_line",
        points: [{ value: initialBid }],
        styles: {
          line: {
            color: bidLineColor,
            size: 1,
            style: "dashed",
          },
          text: {
            color: bidLineColor,
            size: 11,
            backgroundColor: `${bidLineColor}20`,
            borderColor: bidLineColor,
            borderSize: 1,
            paddingLeft: 4,
            paddingRight: 4,
            paddingTop: 2,
            paddingBottom: 2,
            borderRadius: 2,
          },
        },
        extendData: {
          text: initialBid
            ? `BID ${initialBid.toFixed(digits)}`
            : "",
          priceType: "bid",
        },
      });
    } catch (error) {
      console.error("Failed to create ask/bid lines:", error);
    }

    return () => {
      if (!chartInstanceRef.current) return;
      if (askLineIdRef.current) {
        try {
          chartInstanceRef.current.removeOverlay(askLineIdRef.current);
        } catch (error) {
          // ignore
        }
        askLineIdRef.current = null;
      }
      if (bidLineIdRef.current) {
        try {
          chartInstanceRef.current.removeOverlay(bidLineIdRef.current);
        } catch (error) {
          // ignore
        }
        bidLineIdRef.current = null;
      }
    };
  }, [
    selectedSymbol?.currency,
    selectedSymbol?.digits,
    selectedTimeframe,
    showAskPriceLine,
    askLineColor,
    bidLineColor,
  ]);

  // RP-05: per-tick ask/bid overlay updates are folded into the rAF-coalesced
  // live-candle flush (`applyAskBidOverlay`), so no separate per-tick effect.
  // RP-13: the redundant duplicate cleanup effect keyed on [selectedSymbol] was
  // removed — the lifecycle effect above already tears down the overlays on
  // symbol/feature/color change and on unmount.

  // Bar close countdown: keep latest inputs in refs so a single persistent timer
  // can keep ticking across symbol/timeframe changes without restart.
  const bidCountdownTimeframeRef = useRef<string>(selectedTimeframe);
  const bidCountdownFeatureFlagRef = useRef<boolean>(showCountdownToBarClose);

  // `bidCountdownLatestBidRef` is now kept current by the imperative quote
  // subscription (every tick), so no separate `currentQuote`-keyed effect.

  useEffect(() => {
    bidCountdownTimeframeRef.current = selectedTimeframe;
  }, [selectedTimeframe]);

  useEffect(() => {
    bidCountdownFeatureFlagRef.current = showCountdownToBarClose;
  }, [showCountdownToBarClose]);

  // Reset the countdown overlay so the persistent timer recreates it after a
  // symbol or timeframe change.
  //   - Symbol change: the chart instance is rebuilt, so the old overlay is
  //     already gone; just clear the id.
  //   - Timeframe change (RP-03): the chart instance is NOT rebuilt, so we must
  //     explicitly remove the existing overlay before clearing the id, otherwise
  //     the timer would create a second overlay and orphan the old one.
  useEffect(() => {
    const chart = chartInstanceRef.current;
    if (chart && bidCountdownOverlayIdRef.current) {
      removeBidCountdownOverlay(chart, bidCountdownOverlayIdRef.current);
    }
    bidCountdownOverlayIdRef.current = null;
  }, [selectedSymbol?.currency, selectedTimeframe]);

  // Persistent 1s timer for the lifetime of the chart screen.
  useEffect(() => {
    registerBidCountdownOverlay();

    const tick = () => {
      const chart = chartInstanceRef.current;
      if (!chart) return;

      if (!bidCountdownFeatureFlagRef.current) {
        if (bidCountdownOverlayIdRef.current) {
          removeBidCountdownOverlay(chart, bidCountdownOverlayIdRef.current);
          bidCountdownOverlayIdRef.current = null;
        }
        return;
      }

      const bid = bidCountdownLatestBidRef.current;
      const tf = bidCountdownTimeframeRef.current;
      if (!bid || !tf) return;

      const text = formatRemainingMs(
        getNextCandleCloseMs(Date.now(), tf) - Date.now()
      );

      if (!bidCountdownOverlayIdRef.current) {
        bidCountdownOverlayIdRef.current = createBidCountdownOverlay(
          chart,
          bid,
          text
        );
      } else {
        updateBidCountdownOverlay(
          chart,
          bidCountdownOverlayIdRef.current,
          bid,
          text
        );
      }
    };

    tick();
    const intervalId = window.setInterval(tick, 1000);

    return () => {
      window.clearInterval(intervalId);
      const chart = chartInstanceRef.current;
      if (chart && bidCountdownOverlayIdRef.current) {
        removeBidCountdownOverlay(chart, bidCountdownOverlayIdRef.current);
        bidCountdownOverlayIdRef.current = null;
      }
    };
    // Intentionally runs once for the component lifetime; everything else is read
    // through refs so the timer survives symbol and timeframe changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Removed the per-bid countdown smoothing effect that previously fired on
  // every quote tick - the 1s timer above already reads the latest bid via
  // `bidCountdownLatestBidRef`, which is updated by the synchronization effect
  // a few lines up. This avoids a klinecharts overlay update per tick.

  // Separate-pane indicators — incremental diff sync
  const separateWasmTickRef = useRef(customWasmComputeTick);
  useEffect(() => {
    const chart = chartInstanceRef.current;
    if (!chart) return;

    const changed = syncIndicators(
      chart as unknown as ChartIndicatorApi,
      { isOverlay: false },
      (separatePaneIndicators ?? []) as IndicatorSyncItem[],
      separateIndicatorSyncRef.current
    );
    // Custom WASM compute results arrive out-of-band (tick bump), so they
    // still need an explicit recalc even when the diff sync saw no changes.
    const wasmTickChanged = separateWasmTickRef.current !== customWasmComputeTick;
    separateWasmTickRef.current = customWasmComputeTick;
    if (changed || wasmTickChanged) {
      forceChartIndicatorRecalc(chart as unknown as ChartIndicatorApi);
    }
  }, [
    separatePaneIndicators,
    selectedSymbol?.currency,
    selectedTimeframe,
    customWasmComputeTick,
    customCompileRevisionKey,
  ]);

  const handleTextSubmit = () => {
    if (!chartInstanceRef.current || !textValue.trim() || !selectedSymbol)
      return;

    // Utwórz obiekt tekstowy na wykresie
    createDrawing(
      chartInstanceRef.current,
      pendingTextDrawingTypeRef.current,
      {
        symbol: selectedSymbol.currency,
        chartPanelId: String(chartPanelId),
        addInstance: addChartObject,
        updateSettings: updateChartObjectSettings,
        textValue: textValue.trim(),
      }
    );

    // Zamknij dialog i wyczyść wartość
    setShowTextModal(false);
    setTextValue("");
  };

  return (
    <div
      className={`h-full w-full bg-background relative ${
        !isDesktop ? "touch-none select-none" : ""
      }`}
      ref={chartRef}
      onClick={handleChartBackgroundClick}
    >
      {/*
        Proposal 65 — visibility resync gate surfaces. Token-only, pointer-events
        off, absolutely positioned inside the existing chart container (no layout
        change): a per-panel freshness dot, a subtle stale dim while the pump is
        frozen, and a "Resyncing chart · backfilling N bars" chip while the gate
        splices the missing range.
      */}
      <span
        aria-hidden
        title={`chart ${chartFreshness}`}
        className={`pointer-events-none absolute right-2 top-2 z-30 h-1.5 w-1.5 rounded-full ${
          chartFreshness === "live"
            ? "bg-green-500"
            : chartFreshness === "stale"
              ? "bg-yellow-500 animate-pulse"
              : "bg-blue-400 animate-pulse"
        }`}
      />
      {chartFreshness === "stale" && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 z-10 bg-background/40"
        />
      )}
      {chartFreshness === "resyncing" && (
        <div
          role="status"
          className="pointer-events-none absolute left-1/2 top-2 z-30 flex -translate-x-1/2 items-center gap-2 rounded-md border border-blue-400/50 bg-background/90 px-2.5 py-1 font-mono text-[11px] text-blue-400 shadow-lg"
        >
          <Loader2 aria-hidden className="h-3 w-3 shrink-0 animate-spin" />
          <span className="font-semibold tracking-wide">
            Resyncing chart
          </span>
          <span className="text-muted-foreground">
            backfilling {backfilledBars} bars
          </span>
        </div>
      )}
      {isDesktop &&
        (isTradePanelVisible ||
          (isMarketDepthVisible && selectedSymbol?.currency)) && (
          <div
            className="pointer-events-none absolute left-2 top-7 z-20 flex flex-col items-stretch gap-1"
            style={{ width: CHART_DESKTOP_OVERLAY_WIDTH_PX }}
          >
            {isTradePanelVisible && (
              <div className="w-full">
                <ChartTradePanel variant="desktopOverlay" />
              </div>
            )}

            {isMarketDepthVisible && selectedSymbol?.currency && (
              <ChartMarketDepthPanel
                symbol={selectedSymbol.currency}
                digits={selectedSymbol.digits ?? 5}
                width="100%"
                isDesktop
              />
            )}
          </div>
        )}
      {!isDesktop && isMarketDepthVisible && selectedSymbol?.currency && (
        // touch-auto: the wrapper is the klinecharts mount node and carries touch-none on
        // mobile for the chart gestures; without re-enabling it here the depth ladder
        // cannot be flick-scrolled on a phone.
        <div className="absolute inset-x-2 bottom-2 z-20 touch-auto">
          <ChartMarketDepthPanel
            symbol={selectedSymbol.currency}
            digits={selectedSymbol.digits ?? 5}
          />
        </div>
      )}
      {selectedTradeTicket && !isInvestor && (
        <TradeOverlayNew
          ticket={selectedTradeTicket}
          chartInstanceRef={chartInstanceRef}
          placementHints={tradeOverlayPlacementHints}
          onConsumePlacementHints={handleConsumeTradeOverlayPlacementHints}
          onClose={() => setSelectedTradeTicket(null)}
          isDesktop={isDesktop}
        />
      )}
      {/*
        Conditionally mount the panels/dialogs below. When they are not
        visible they previously stayed in the tree (just CSS-hidden) and kept
        running their hooks (`useOrderLinesNew`, settings-store subscriptions,
        etc.) on every Chart re-render. With this gating they have zero cost
        while the user is just looking at the chart.
      */}
      {setIsSpecificTradePanelVisible && isSpecificTradePanelVisible && !isInvestor && (
        <SpecificTradePanel
          onClose={() => setIsSpecificTradePanelVisible(false)}
          chartInstanceRef={chartInstanceRef}
          currentQuote={currentQuote}
          isVisible={isSpecificTradePanelVisible}
          onOrderLinePriceChange={onOrderLinePriceChange}
          selectedSymbol={selectedSymbol?.currency}
          isDesktop={isDesktop}
        />
      )}

      {showTextModal && (
        <AddTextDialog
          open={showTextModal}
          onOpenChange={setShowTextModal}
          textValue={textValue}
          onTextValueChange={setTextValue}
          onSubmit={handleTextSubmit}
        />
      )}

      {showOneClickDialog && (
        <OneClickTradingDialog
          open={showOneClickDialog}
          onOpenChange={handleOneClickDialogChange}
          onConfirm={handleOneClickCloseConfirm}
        />
      )}

    </div>
  );
}
